import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import {
  BarChart3,
  TrendingUp,
  Package,
  AlertTriangle,
  Briefcase,
  DollarSign,
  Clock,
  Wrench,
} from 'lucide-react';

interface InventorySummary {
  category_id: string;
  category_name: string;
  definition_count: number;
  units_available: number;
  units_checked_out: number;
  bin_quantity: number;
  total_value: number;
}

interface LowStockItem {
  id: string;
  name: string;
  part_number?: string;
  manufacturer?: string;
  source_url?: string;
  unit_cost?: number;
  min_quantity: number;
  reorder_quantity?: number;
  current_quantity: number;
}

interface UsageTrend {
  date: string;
  usage_count: number;
  total_quantity: number;
  total_cost: number;
}

interface CostByJob {
  job_id: string;
  job_number: string;
  customer?: string;
  part_number?: string;
  status: string;
  usage_count: number;
  total_quantity: number;
  total_cost: number;
  total_chip_time: number;
}

interface CostByTool {
  definition_id: string;
  name: string;
  part_number?: string;
  manufacturer?: string;
  unit_cost?: number;
  usage_count: number;
  total_quantity: number;
  total_cost: number;
  total_chip_time: number;
  avg_life_hours?: number;
}

export function Reports() {
  const [activeTab, setActiveTab] = useState<'inventory' | 'lowStock' | 'usage' | 'costByJob' | 'costByTool'>('inventory');
  const [trendDays, setTrendDays] = useState('30');

  // Dashboard stats
  const { data: dashboardStats } = useQuery({
    queryKey: ['reports', 'dashboard'],
    queryFn: async () => {
      const res = await api.get<{
        totalTools: number;
        activeCheckouts: number;
        lowStockAlerts: number;
        activeJobs: number;
      }>('/reports/dashboard');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  // Inventory summary
  const { data: inventorySummary, isLoading: loadingInventory } = useQuery({
    queryKey: ['reports', 'inventory-summary'],
    queryFn: async () => {
      const res = await api.get<InventorySummary[]>('/reports/inventory-summary');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
    enabled: activeTab === 'inventory',
  });

  // Low stock
  const { data: lowStock, isLoading: loadingLowStock } = useQuery({
    queryKey: ['reports', 'low-stock'],
    queryFn: async () => {
      const res = await api.get<LowStockItem[]>('/reports/low-stock');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
    enabled: activeTab === 'lowStock',
  });

  // Usage trends
  const { data: usageTrends, isLoading: loadingTrends } = useQuery({
    queryKey: ['reports', 'usage-trends', trendDays],
    queryFn: async () => {
      const res = await api.get<UsageTrend[]>(`/reports/usage-trends?days=${trendDays}`);
      if (res.success) return res.data;
      throw new Error(res.error);
    },
    enabled: activeTab === 'usage',
  });

  // Cost by job
  const { data: costByJob, isLoading: loadingCostByJob } = useQuery({
    queryKey: ['reports', 'cost-by-job'],
    queryFn: async () => {
      const res = await api.get<CostByJob[]>('/reports/cost-by-job');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
    enabled: activeTab === 'costByJob',
  });

  // Cost by tool
  const { data: costByTool, isLoading: loadingCostByTool } = useQuery({
    queryKey: ['reports', 'cost-by-tool'],
    queryFn: async () => {
      const res = await api.get<CostByTool[]>('/reports/cost-by-tool');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
    enabled: activeTab === 'costByTool',
  });

  const formatCurrency = (value: number | null | undefined) => {
    if (value == null) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const tabs = [
    { id: 'inventory', label: 'Inventory Summary', icon: Package },
    { id: 'lowStock', label: 'Low Stock', icon: AlertTriangle },
    { id: 'usage', label: 'Usage Trends', icon: TrendingUp },
    { id: 'costByJob', label: 'Cost by Job', icon: Briefcase },
    { id: 'costByTool', label: 'Cost by Tool', icon: Wrench },
  ] as const;

  const inventoryTotals = inventorySummary?.reduce(
    (acc, item) => ({
      definitions: acc.definitions + (item.definition_count || 0),
      available: acc.available + (item.units_available || 0) + (item.bin_quantity || 0),
      checkedOut: acc.checkedOut + (item.units_checked_out || 0),
      value: acc.value + (item.total_value || 0),
    }),
    { definitions: 0, available: 0, checkedOut: 0, value: 0 }
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reports</h1>
          <p className="text-foreground-muted">Analytics and insights for your tool inventory</p>
        </div>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Package className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-foreground-muted">Total Tools</p>
              <p className="text-2xl font-bold">{dashboardStats?.totalTools || 0}</p>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="p-3 bg-warning/10 rounded-lg">
              <Clock className="h-6 w-6 text-warning" />
            </div>
            <div>
              <p className="text-sm text-foreground-muted">Active Checkouts</p>
              <p className="text-2xl font-bold">{dashboardStats?.activeCheckouts || 0}</p>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="p-3 bg-error/10 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-error" />
            </div>
            <div>
              <p className="text-sm text-foreground-muted">Low Stock Alerts</p>
              <p className="text-2xl font-bold">{dashboardStats?.lowStockAlerts || 0}</p>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="p-3 bg-success/10 rounded-lg">
              <Briefcase className="h-6 w-6 text-success" />
            </div>
            <div>
              <p className="text-sm text-foreground-muted">Active Jobs</p>
              <p className="text-2xl font-bold">{dashboardStats?.activeJobs || 0}</p>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-4">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'primary' : 'ghost'}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'inventory' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Inventory Summary by Category
            </CardTitle>
          </CardHeader>
          <CardBody>
            {loadingInventory ? (
              <div className="text-center py-12">
                <div className="spinner h-8 w-8 mx-auto" />
              </div>
            ) : (
              <>
                {/* Totals */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-surface-hover rounded-lg">
                  <div>
                    <p className="text-sm text-foreground-muted">Tool Types</p>
                    <p className="text-xl font-bold">{inventoryTotals?.definitions || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground-muted">Available</p>
                    <p className="text-xl font-bold text-success">{inventoryTotals?.available || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground-muted">Checked Out</p>
                    <p className="text-xl font-bold text-warning">{inventoryTotals?.checkedOut || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground-muted">Total Value</p>
                    <p className="text-xl font-bold">{formatCurrency(inventoryTotals?.value)}</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th className="text-right">Tool Types</th>
                        <th className="text-right">Available</th>
                        <th className="text-right">Checked Out</th>
                        <th className="text-right">Total Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventorySummary?.map((item) => (
                        <tr key={item.category_id}>
                          <td className="font-medium">{item.category_name}</td>
                          <td className="text-right">{item.definition_count || 0}</td>
                          <td className="text-right text-success">
                            {(item.units_available || 0) + (item.bin_quantity || 0)}
                          </td>
                          <td className="text-right text-warning">{item.units_checked_out || 0}</td>
                          <td className="text-right">{formatCurrency(item.total_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      )}

      {activeTab === 'lowStock' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-error" />
              Low Stock Items
            </CardTitle>
          </CardHeader>
          <CardBody>
            {loadingLowStock ? (
              <div className="text-center py-12">
                <div className="spinner h-8 w-8 mx-auto" />
              </div>
            ) : lowStock?.length === 0 ? (
              <div className="text-center py-12 text-foreground-muted">
                <Package className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium mb-2">No low stock items</p>
                <p className="text-sm">All inventory levels are above minimum thresholds</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th className="text-right">Current</th>
                      <th className="text-right">Minimum</th>
                      <th className="text-right">Reorder Qty</th>
                      <th className="text-right">Unit Cost</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowStock?.map((item) => {
                      const stockRatio = item.current_quantity / item.min_quantity;
                      return (
                        <tr key={item.id}>
                          <td>
                            <div>
                              <p className="font-medium">{item.name}</p>
                              {item.part_number && (
                                <p className="text-sm text-foreground-muted">{item.part_number}</p>
                              )}
                              {item.manufacturer && (
                                <p className="text-xs text-foreground-muted">{item.manufacturer}</p>
                              )}
                            </div>
                          </td>
                          <td className="text-right font-bold text-error">{item.current_quantity}</td>
                          <td className="text-right">{item.min_quantity}</td>
                          <td className="text-right">{item.reorder_quantity || '-'}</td>
                          <td className="text-right">{formatCurrency(item.unit_cost)}</td>
                          <td>
                            {stockRatio === 0 ? (
                              <Badge variant="error">Out of Stock</Badge>
                            ) : stockRatio < 0.5 ? (
                              <Badge variant="error">Critical</Badge>
                            ) : (
                              <Badge variant="warning">Low</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {activeTab === 'usage' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Usage Trends
            </CardTitle>
            <Select
              options={[
                { value: '7', label: 'Last 7 days' },
                { value: '30', label: 'Last 30 days' },
                { value: '90', label: 'Last 90 days' },
                { value: '365', label: 'Last year' },
              ]}
              value={trendDays}
              onChange={(e) => setTrendDays(e.target.value)}
              className="w-40"
            />
          </CardHeader>
          <CardBody>
            {loadingTrends ? (
              <div className="text-center py-12">
                <div className="spinner h-8 w-8 mx-auto" />
              </div>
            ) : usageTrends?.length === 0 ? (
              <div className="text-center py-12 text-foreground-muted">
                <BarChart3 className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium mb-2">No usage data</p>
                <p className="text-sm">Tool usage will appear here once tools are checked out and returned</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="text-right">Usage Count</th>
                      <th className="text-right">Quantity</th>
                      <th className="text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageTrends?.map((trend) => (
                      <tr key={trend.date}>
                        <td className="font-medium">{new Date(trend.date).toLocaleDateString()}</td>
                        <td className="text-right">{trend.usage_count}</td>
                        <td className="text-right">{trend.total_quantity}</td>
                        <td className="text-right">{formatCurrency(trend.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {activeTab === 'costByJob' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Tool Cost by Job
            </CardTitle>
          </CardHeader>
          <CardBody>
            {loadingCostByJob ? (
              <div className="text-center py-12">
                <div className="spinner h-8 w-8 mx-auto" />
              </div>
            ) : costByJob?.length === 0 ? (
              <div className="text-center py-12 text-foreground-muted">
                <DollarSign className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium mb-2">No cost data</p>
                <p className="text-sm">Job costs will appear once tools are used on jobs</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Customer</th>
                      <th className="text-right">Usage Count</th>
                      <th className="text-right">Quantity</th>
                      <th className="text-right">Chip Time</th>
                      <th className="text-right">Total Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costByJob?.map((job) => (
                      <tr key={job.job_id}>
                        <td>
                          <div>
                            <p className="font-medium">{job.job_number}</p>
                            {job.part_number && (
                              <p className="text-sm text-foreground-muted">{job.part_number}</p>
                            )}
                          </div>
                        </td>
                        <td className="text-foreground-muted">{job.customer || '-'}</td>
                        <td className="text-right">{job.usage_count || 0}</td>
                        <td className="text-right">{job.total_quantity || 0}</td>
                        <td className="text-right">
                          {job.total_chip_time ? `${job.total_chip_time.toFixed(1)}h` : '-'}
                        </td>
                        <td className="text-right font-medium">{formatCurrency(job.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {activeTab === 'costByTool' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Tool Cost Analysis
            </CardTitle>
          </CardHeader>
          <CardBody>
            {loadingCostByTool ? (
              <div className="text-center py-12">
                <div className="spinner h-8 w-8 mx-auto" />
              </div>
            ) : costByTool?.length === 0 ? (
              <div className="text-center py-12 text-foreground-muted">
                <DollarSign className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium mb-2">No cost data</p>
                <p className="text-sm">Tool costs will appear once tools are used</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Tool</th>
                      <th className="text-right">Unit Cost</th>
                      <th className="text-right">Usage Count</th>
                      <th className="text-right">Quantity Used</th>
                      <th className="text-right">Avg Life (hrs)</th>
                      <th className="text-right">Total Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costByTool?.map((tool) => (
                      <tr key={tool.definition_id}>
                        <td>
                          <div>
                            <p className="font-medium">{tool.name}</p>
                            {tool.part_number && (
                              <p className="text-sm text-foreground-muted">{tool.part_number}</p>
                            )}
                            {tool.manufacturer && (
                              <p className="text-xs text-foreground-muted">{tool.manufacturer}</p>
                            )}
                          </div>
                        </td>
                        <td className="text-right">{formatCurrency(tool.unit_cost)}</td>
                        <td className="text-right">{tool.usage_count || 0}</td>
                        <td className="text-right">{tool.total_quantity || 0}</td>
                        <td className="text-right">
                          {tool.avg_life_hours ? `${tool.avg_life_hours.toFixed(1)}` : '-'}
                        </td>
                        <td className="text-right font-medium">{formatCurrency(tool.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
